import { FunctionComponent, useCallback } from 'react'

import styled, { css, useTheme } from 'styled-components'

import { mobileCSS } from '@measures/responsive'
import translate from '@i18n'
import useBlickPlusOnboardingTracking from '@hooks/useBlickPlusOnboardingTracking'
import useViewportTracking from '@hooks/useViewportTracking'
import IconButton from '@components/Buttons/IconButton'
import PrimaryCTAButton from '@components/Buttons/PrimaryCTAButton'
import SecondaryCTAButton from '@components/Buttons/SecondaryCTAButton'

interface WelcomePageProps {
  onClose: (buttonText: string) => void
  onClickNext: (buttonText: string) => void
}

const StyledWrapper = styled.div`
  ${({
    theme: {
      colors: { fillDefault },
      spacing: { spacing16, spacing24, spacing40, spacing96 },
    },
  }) => css`
    position: relative;
    display: flex;
    flex-direction: column;
    box-sizing: border-box;

    max-width: 632px;
    width: auto;
    margin: auto 0;

    background-color: ${fillDefault};
    padding: ${spacing40} ${spacing96};
    gap: ${spacing24};
    ${mobileCSS(css`
      padding: ${spacing24} ${spacing16};
      width: calc(100% - 2 * ${spacing16});
    `)}
  `}
`

const StyledHeaderWrapper = styled.div`
  ${({
    theme: {
      spacing: { spacing12 },
    },
  }) => css`
    display: flex;
    flex-direction: row;
    gap: ${spacing12};
    align-items: flex-start;
  `}
`

const StyledHeaderText = styled.div`
  ${({
    theme: {
      typography: {
        subheads: {
          xlarge: { bundledCSS: xlargeSubheadCSS },
          large1: { bundledCSS: large1SubheadCSS },
        },
      },
      colors: { textDefault },
    },
  }) => css`
    ${xlargeSubheadCSS};
    color: ${textDefault};
    flex-grow: 1;
    ${mobileCSS(large1SubheadCSS)}
  `}
`

const StyledButtonWrapper = styled.div`
  ${({
    theme: {
      spacing: { spacing12 },
    },
  }) => css`
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: ${spacing12};
  `}
`

const StyledPrimaryButton = styled(PrimaryCTAButton)`
  width: 162px;
  ${mobileCSS(css`
    width: 100%;
  `)}
`

const StyledSecondaryButton = styled(SecondaryCTAButton)`
  width: 162px;
  ${mobileCSS(css`
    width: 100%;
  `)}
`

const WelcomePage: FunctionComponent<WelcomePageProps> = ({
  onClickNext,
  onClose,
}) => {
  const theme = useTheme()
  const handleButtonClose = useCallback(() => {
    onClose(translate('blickPlusOnboardingModal.welcomePage.cancelButton'))
  }, [onClose])
  const handleButtonClick = useCallback(() => {
    onClickNext(translate('blickPlusOnboardingModal.welcomePage.nextButton'))
  }, [onClickNext])

  const { trackOnImpression } = useBlickPlusOnboardingTracking()

  const viewportRef = useViewportTracking({
    onImpression: trackOnImpression,
    track: true,
  })
  return (
    <StyledWrapper ref={viewportRef}>
      <StyledHeaderWrapper>
        <StyledHeaderText>
          {translate('blickPlusOnboardingModal.welcomePageTitle')}
          <StyledHeaderText>
            {translate('blickPlusOnboardingModal.welcomePageSubtitle')}
          </StyledHeaderText>
        </StyledHeaderText>
        <IconButton
          ariaLabel="Onboarding Modal Close"
          onClick={() => onClose('close')}
          iconName="xmark-large"
          iconSize={24}
          iconColor={theme.colors.iconDefault}
          buttonSize={24}
        />
      </StyledHeaderWrapper>
      <StyledButtonWrapper>
        <StyledPrimaryButton onClick={handleButtonClick} size="large">
          {translate('blickPlusOnboardingModal.welcomePage.nextButton')}
        </StyledPrimaryButton>
        <StyledSecondaryButton onClick={handleButtonClose} size="large">
          {translate('blickPlusOnboardingModal.welcomePage.cancelButton')}
        </StyledSecondaryButton>
      </StyledButtonWrapper>
    </StyledWrapper>
  )
}

export default WelcomePage
